import { ExtendedWoPart, PartEditData } from "../../../hooks/parts/useWorkorderParts";

/**
 * L'état du formulaire après un basculement Camion <-> Comptoir.
 *
 * Extrait de la ligne de tableau pour que le formulaire en modale se comporte exactement pareil :
 * c'est la règle la plus subtile de cet écran (l'identifiant de la pièce catalogue doit être
 * effacé en passant au comptoir, sans quoi on écrase la pièce du catalogue) et deux copies
 * finiraient par diverger.
 *
 * @param partItem la ligne telle qu'enregistrée, pour revenir à son état d'origine.
 */
export function nextStateForDeskToggle(
  current: PartEditData,
  isChecked: boolean,
  partItem: ExtendedWoPart,
  computeSellingPrice?: (cost: number) => number
): PartEditData {
  // Revenir à la valeur déjà enregistrée : on restaure la ligne telle quelle.
  if (isChecked === partItem.desk) {
    let restoredPart = partItem.part;
    if (!isChecked && restoredPart?.ref?.startsWith("EXT_")) {
      restoredPart = { ...restoredPart, ref: restoredPart.ref.replace(/^EXT_/, "") };
    }
    return {
      ...current,
      desk: isChecked,
      part: restoredPart,
      searchQuery: restoredPart?.label || "",
      price: partItem.price,
      purchasePrice: partItem.purchasePrice,
      quantity: partItem.quantity,
    };
  }

  if (isChecked) {
    // Passage au comptoir : on repart des champs saisis, mais sans l'identifiant catalogue —
    // sinon l'enregistrement écraserait la pièce de référence.
    const searchQuery = current.part?.label || current.searchQuery || "";
    const oldRef = current.part?.ref || "";
    const purchasePrice = current.purchasePrice || current.part?.purchasePrice || 0;
    let price = current.price || current.part?.price || 0;
    if (computeSellingPrice && purchasePrice && !price) {
      const calculated = computeSellingPrice(purchasePrice);
      price = calculated ? Number(calculated.toFixed(2)) : 0;
    }

    return {
      ...current,
      desk: true,
      originalPart: current.part,
      part: {
        ...(current.part || {}),
        id: undefined,
        label: searchQuery,
        ref: oldRef ? (oldRef.startsWith("EXT_") ? oldRef : `EXT_${oldRef}`) : "",
      } as any,
      searchQuery,
      purchasePrice,
      price,
    };
  }

  // Retour au camion : on reprend la pièce d'origine si on en a une.
  let part = current.originalPart;
  let searchQuery: string;
  let price: number;
  let purchasePrice: number;
  let quantity = current.quantity;

  if (current.originalPart !== undefined) {
    searchQuery = part?.label || "";
    price = part?.price || current.price || 0;
    purchasePrice = part?.purchasePrice || current.purchasePrice || 0;
    quantity = current.quantity || 1;
  } else if (partItem.part && !partItem.desk) {
    part = partItem.part;
    searchQuery = partItem.part.label || "";
    price = partItem.price || partItem.part.price || 0;
    purchasePrice = partItem.purchasePrice || partItem.part.purchasePrice || 0;
    quantity = partItem.quantity || 1;
  } else {
    part = null;
    searchQuery = current.part?.label || current.searchQuery || "";
    price = 0;
    purchasePrice = 0;
    quantity = current.quantity || 1;
  }

  if (part?.ref?.startsWith("EXT_")) {
    part = { ...part, ref: part.ref.replace(/^EXT_/, "") };
  }

  return {
    ...current,
    desk: false,
    part,
    searchQuery,
    price,
    purchasePrice,
    quantity,
    originalPart: undefined,
  };
}
